import { mapRowsInCache } from './list-cache-rows';

/**
 * Walks every shape a React Query list cache can hold for a row-keyed
 * resource - an infinite-query cache (`{ pages, pageParams }`) or a
 * single-page `PaginatedResponse<T>` - and replaces the row matching
 * `updatedRow.id` in place. Untouched rows keep referential identity; the
 * page and cache objects around them are rebuilt on every call.
 *
 * Used as the updater for a `queryClient.setQueriesData({ queryKey }, ...)`
 * call after a mutation returns the row it changed, so every cached variant
 * (every filter/sort combination matching the key prefix) reflects the
 * mutation's own response immediately - no invalidation of the affected
 * infinite query, per `.claude/rules/no-blanket-invalidate-infinite-query.md`.
 *
 * Domain-specific callers (`applyInvoiceUpdateToCache`,
 * `applyLineUpdateToCache`) are thin, separately-named wrappers around this:
 * they exist so each call site keeps importing a name that says what it
 * updates, not because the walk differs between resources.
 */
export function applyRowUpdateToCache<T extends { id: string }>(
  old: unknown,
  updatedRow: T,
  merge: (cached: T, updated: T) => T = (_cached, updated) => updated,
): unknown {
  return mapRowsInCache<T>(old, (row) => (row.id === updatedRow.id ? merge(row, updatedRow) : row));
}
